import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterToolbar = ({ filters, onFiltersChange, resultCount, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const registryOptions = [
    { value: 'all', label: 'All Registries' },
    { value: 'verra', label: 'Verra' },
    { value: 'gold-standard', label: 'Gold Standard' },
    { value: 'acr', label: 'American Carbon Registry' },
    { value: 'art', label: 'Architecture for REDD+ Transactions' },
    { value: 'gcc', label: 'Global Carbon Council' },
    { value: 'cad-trust', label: 'CAD Trust' }
  ];

  const methodologyOptions = [
    { value: 'all', label: 'All Methodologies' },
    { value: 'forestry', label: 'Forestry & Land Use' },
    { value: 'renewable-energy', label: 'Renewable Energy' },
    { value: 'energy-efficiency', label: 'Energy Efficiency' },
    { value: 'waste-management', label: 'Waste Management' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'industrial', label: 'Industrial Processes' },
    { value: 'transport', label: 'Transportation' }
  ];

  const integrityOptions = [
    { value: 'all', label: 'All Integrity Levels' },
    { value: 'high', label: 'High Integrity' },
    { value: 'medium', label: 'Medium Integrity' },
    { value: 'low', label: 'Low Integrity' },
    { value: 'pending', label: 'Pending Assessment' }
  ];

  const ccpStatusOptions = [
    { value: 'all', label: 'All CCP Status' },
    { value: 'approved', label: 'CCP Approved' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'not-approved', label: 'Not Approved' },
    { value: 'not-applicable', label: 'Not Applicable' }
  ];

  const vintageOptions = [
    { value: 'all', label: 'All Vintages' },
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' },
    { value: '2022', label: '2022' },
    { value: '2021', label: '2021' },
    { value: '2020', label: '2020' },
    { value: 'older', label: '2019 and older' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value !== 'all' && value !== '' && value !== null
  );

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-card-foreground">Filter Credits</h3>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Filter" size={16} />
            <span>{resultCount?.toLocaleString()} credits found</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClearFilters}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden"
          >
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Select
          label="Registry"
          options={registryOptions}
          value={filters?.registry || 'all'}
          onChange={(value) => handleFilterChange('registry', value)}
          className="w-full"
        />
        
        <Select
          label="Methodology Category"
          options={methodologyOptions}
          value={filters?.methodology || 'all'}
          onChange={(value) => handleFilterChange('methodology', value)}
          className="w-full"
        />
        
        <Select
          label="Integrity Level"
          options={integrityOptions}
          value={filters?.integrity || 'all'}
          onChange={(value) => handleFilterChange('integrity', value)}
          className="w-full"
        />
        
        <Select
          label="CCP Status"
          options={ccpStatusOptions}
          value={filters?.ccpStatus || 'all'}
          onChange={(value) => handleFilterChange('ccpStatus', value)}
          className="w-full"
        />
      </div>
      {/* Advanced Filters - Expandable on Mobile */}
      <div className={`space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="Vintage Year"
            options={vintageOptions}
            value={filters?.vintage || 'all'}
            onChange={(value) => handleFilterChange('vintage', value)}
            className="w-full"
          />
          
          <Input
            label="Serial Number"
            type="text"
            placeholder="Search by serial..."
            value={filters?.serialNumber || ''}
            onChange={(e) => handleFilterChange('serialNumber', e?.target?.value)}
            className="w-full"
          />
          
          <Input
            label="Project Name"
            type="text"
            placeholder="Search projects..."
            value={filters?.projectName || ''}
            onChange={(e) => handleFilterChange('projectName', e?.target?.value)}
            className="w-full"
          />
          
          <Input
            label="Location"
            type="text"
            placeholder="Country or region..."
            value={filters?.location || ''}
            onChange={(e) => handleFilterChange('location', e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Quantity Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            label="Min Quantity (tCO2e)"
            type="number"
            placeholder="0"
            value={filters?.minQuantity || ''}
            onChange={(e) => handleFilterChange('minQuantity', e?.target?.value)}
            className="w-full"
          />
          
          <Input
            label="Max Quantity (tCO2e)"
            type="number"
            placeholder="1000000"
            value={filters?.maxQuantity || ''}
            onChange={(e) => handleFilterChange('maxQuantity', e?.target?.value)}
            className="w-full"
          />
          
          <div className="flex items-center space-x-4 pt-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters?.availableOnly || false}
                onChange={(e) => handleFilterChange('availableOnly', e?.target?.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-card-foreground">Available only</span>
            </label>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters?.tokenized || false}
                onChange={(e) => handleFilterChange('tokenized', e?.target?.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-card-foreground">Tokenized</span>
            </label>
          </div>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            {Object.entries(filters)?.map(([key, value]) => {
              if (value && value !== 'all' && value !== '') {
                return (
                  <span
                    key={key}
                    className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs"
                  >
                    <span>{key}: {value}</span>
                    <button
                      onClick={() => handleFilterChange(key, key === 'availableOnly' || key === 'tokenized' ? false : 'all')}
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterToolbar;